/**
 * Creates a {@link Record} by mapping the given `items` to a specified key.
 * An entry's value will be corresponding `item` itself.
 *
 * @param items - Items to create a record from.
 * @param keyFn - Function to select the key from the item.
 */
export function createRecord<
	TKey extends string | number | symbol,
	const TArray extends ReadonlyArray<unknown>,
	TItem = TArray[number],
>(items: TArray, keyFn: (item: TItem) => TKey): Record<TKey, TItem>;

/**
 * Creates a {@link Record} by mapping the given `items` to a specified key and value.
 * An entry's value will be the result of the `valueFn` function.
 *
 * @param items - Items to create a record from.
 * @param keyFn - Function to select the key from the item.
 * @param valueFn - Function to map the item to a value.
 */
export function createRecord<
	TKey extends string | number | symbol,
	TValue,
	const TArray extends ReadonlyArray<unknown>,
	TItem = TArray[number],
>(
	items: TArray,
	keyFn: (item: TItem) => TKey,
	valueFn: (item: TItem, index: number, record: Record<TKey, TValue>) => TValue,
): Record<TKey, TValue>;

export function createRecord<
	TKey extends string | number | symbol,
	TValue,
	const TArray extends ReadonlyArray<unknown>,
	TItem = TArray[number],
>(
	items: Readonly<TItem[]>,
	keyFn: (item: TItem) => TKey,
	valueFn?: (item: TItem, index: number, record: Record<TKey, TValue>) => TValue,
): Record<TKey, TValue> {
	return items.reduce(
		(record, item, index) => {
			record[keyFn(item)] = valueFn ? valueFn(item, index, record) : (item as unknown as TValue);

			return record;
		},
		{} as Record<TKey, TValue>,
	);
}
